import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

export interface IamOrganizationRoleMap {
  [organizationId: string]: string[];
}

export interface IamOrganization {
  itemId: string;
  name: string;
}

export interface IamData {
  itemId: string;
  createdDate: string;
  lastUpdatedDate: string;
  lastLoggedInTime: string;
  lastLoggedInDeviceInfo?: string | null;
  logInCount?: number;
  language: string;
  salutation: string;
  firstName: string;
  lastName: string | null;
  email: string;
  userName: string;
  phoneNumber: string | null;
  organizationIds?: string[];
  lastUsedOrganizationId?: string | null;
  country?: string;
  roles: string[];
  permissions: string[];
  active: boolean;
  status?: number;
  statusReason?: string | null;
  deactivatedAtUtc?: string | null;
  isVerified?: boolean;
  isVarified: boolean;
  emailVerifiedAtUtc?: string | null;
  phoneVerifiedAtUtc?: string | null;
  profileImageUrl: string | null;
  mfaEnabled: boolean;
  isMfaVerified?: boolean;
  userMfaType?: number;
  provisioningSource?: number;
  externalIdentities?: unknown[];
  userCreationType?: number;
  department?: string | null;
  employeeId?: string | null;
  isMultiOrgEnabled?: boolean;
  organizations: IamOrganization[];
  plan: {
    itemId: string;
    name: string;
  };
  planItem: {
    itemId: string;
    title: string | null;
  };
  groups: UserGroup[];
}

export interface IamApiData {
  itemId: string;
  createdDate: string;
  lastUpdatedDate?: string;
  lastLoggedInTime?: string;
  lastLoggedInDeviceInfo?: string | null;
  logInCount?: number;
  loginCount?: number;
  language?: string;
  salutation?: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  userName: string;
  phoneNumber?: string | null;
  organizationIds?: string[];
  lastUsedOrganizationId?: string | null;
  roles?: IamOrganizationRoleMap;
  permissions?: IamOrganizationRoleMap;
  active: boolean;
  status?: number;
  statusReason?: string | null;
  deactivatedAtUtc?: string | null;
  isVerified?: boolean;
  emailVerifiedAtUtc?: string | null;
  phoneVerifiedAtUtc?: string | null;
  profileImageUrl?: string | null;
  mfaEnabled?: boolean;
  isMfaVerified?: boolean;
  userMfaType?: number;
  provisioningSource?: number;
  externalIdentities?: unknown[];
  userCreationType?: number;
  department?: string | null;
  employeeId?: string | null;
  isMultiOrgEnabled?: boolean;
  organizations?: IamOrganization[];
  plan?: {
    itemId: string;
    name: string;
  };
  planItem?: {
    itemId: string;
    title: string | null;
  };
  groups?: UserGroup[];
}

export interface UserGroup {
  itemId: string;
  name: string;
}

export interface UserFilter {
  email?: string;
  name?: string;
  status?: {
    active?: boolean;
    inactive?: boolean;
  };
  mfa?: {
    enabled?: boolean;
    disabled?: boolean;
  };
  joinedOn?: string;
  lastLogin?: string;
  org_id?: string;
  organizationId?: string;
  organization?: string;
  userIds?: string[];
  excludeGroupId?: string;
  IsOrgSpecific?: boolean;
  organizationIds?: string[];
}

export interface GetUsersPayload {
  page: number;
  pageSize: number;
  filter?: UserFilter;
  sort?: {
    property: string;
    isDescending: boolean;
  };
}

const normalizeOrganizationFilter = (filter: UserFilter | undefined): string => {
  if(!filter) return '';

  if(filter.org_id && typeof(filter.org_id) === 'string') {
    return filter.org_id;
  }

  if(Array.isArray(filter.organizationIds) && filter.organizationIds.length > 0) {
    return filter.organizationIds[0] ?? '';
  }

  if(filter.organizationId && typeof(filter.organizationId) === 'string') {
    return filter.organizationId;
  }

  if(filter.organization && typeof(filter.organization) === 'string') {
    return filter.organization;
  }

  return '';
};

const flattenOrganizationValues = (values: IamOrganizationRoleMap | undefined): string[] => {
  if(!values) return [];

  return Array.from(
    new Set(
      Object.values(values)
        .flat()
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
    )
  );
};

const normalizeIamUser = (user: IamApiData): IamData => ({
  ...user,
  lastUpdatedDate: user.lastUpdatedDate ?? user.createdDate,
  lastLoggedInTime: user.lastLoggedInTime ?? '',
  logInCount: user.logInCount ?? user.loginCount ?? 0,
  language: user.language ?? '',
  salutation: user.salutation ?? '',
  firstName: user.firstName ?? '',
  phoneNumber: user.phoneNumber || null,
  organizationIds: user.organizationIds ?? [],
  lastUsedOrganizationId: user.lastUsedOrganizationId ?? null,
  active: user.active ?? false,
  status: user.status,
  statusReason: user.statusReason ?? null,
  deactivatedAtUtc: user.deactivatedAtUtc ?? null,
  isVerified: user.isVerified ?? false,
  roles: flattenOrganizationValues(user.roles),
  permissions: flattenOrganizationValues(user.permissions),
  isVarified: user.isVerified ?? false,
  emailVerifiedAtUtc: user.emailVerifiedAtUtc ?? null,
  phoneVerifiedAtUtc: user.phoneVerifiedAtUtc ?? null,
  profileImageUrl: user.profileImageUrl ?? null,
  mfaEnabled: user.mfaEnabled ?? false,
  isMfaVerified: user.isMfaVerified,
  userMfaType: user.userMfaType,
  provisioningSource: user.provisioningSource,
  externalIdentities: user.externalIdentities ?? [],
  userCreationType: user.userCreationType,
  department: user.department ?? null,
  employeeId: user.employeeId ?? null,
  isMultiOrgEnabled: user.isMultiOrgEnabled,
  organizations: user.organizations ?? [],
  plan: user.plan ?? {
    itemId: '',
    name: '',
  },
  planItem: user.planItem ?? {
    itemId: '',
    title: null,
  },
  groups: user.groups ?? [],
});

export const getUsers = (payload: GetUsersPayload, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getUsers}`;
  const organizationId = normalizeOrganizationFilter(payload.filter);
  const requestBody = {
    page: payload.page,
    pageSize: payload.pageSize,
    sort: {
      property: payload.sort?.property ?? 'FirstName',
      isDescending: payload.sort?.isDescending ?? false,
    },
    filter: {
      email: payload.filter?.email,
      name: payload.filter?.name,
      userIds: payload.filter?.userIds,
      status: payload.filter?.status,
      mfa: payload.filter?.mfa,
      joinedOn: payload.filter?.joinedOn,
      lastLogin: payload.filter?.lastLogin,
      organizationIds: [organizationId || undefined],
      excludeGroupId: payload.filter?.excludeGroupId,
      IsOrgSpecific: payload.filter?.IsOrgSpecific,
    },
  };

  return clients.post<{
    data: IamApiData[];
    totalCount: number;
  }>(url, config.blocksKey, JSON.stringify(requestBody)).then((response) => ({
    ...response,
    data: response.data.map(normalizeIamUser),
  }));
};

export interface RoleData {
  id: string;
  name: string;
  description: string;
  createdDate: string;
  lastUpdatedDate: string;
  permissions: string[];
  isDefault: boolean;
  active: boolean;
}

export interface RoleSort {
  property: string;
  isDescending: boolean;
}

export interface RoleFilter {
  search?: string;
}

export interface GetRolesPayload {
  page: number;
  pageSize: number;
  sort?: RoleSort;
  filter?: RoleFilter;
  projectKey?: string;
}

/**
 * Function to fetch a list of roles from the API with pagination, sorting, and filtering.
 *
 * @param {GetRolesPayload} payload - The payload containing pagination, sorting, and filter options.
 * @returns {Promise<{ data: RoleData[], totalCount: number }>} - A promise that resolves to an object containing the list of roles and the total count.
 */
export const getRoles = (payload: GetRolesPayload, config: ConfigurationSettings) => {
  const requestBody = {
    page: payload.page,
    pageSize: payload.pageSize,
    sort: payload.sort
      ? {
          property: payload.sort.property,
          isDescending: payload.sort.isDescending,
        }
      : undefined,
    filter: {
      search: payload.filter?.search ?? '',
    },
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getRoles}`;

  return clients.post<{
    data: RoleData[];
    totalCount: number;
  }>(url, config.blocksKey, JSON.stringify(requestBody)).then((response) => {
    if (response.data.length === 0) {
      return {
        data: [
          {
            id: '1',
            name: 'user',
            description: 'Default user role',
            createdDate: new Date().toISOString(),
            lastUpdatedDate: new Date().toISOString(),
            permissions: [],
            isDefault: true,
            active: true,
          },
          {
            id: '2',
            name: 'admin',
            description: 'Administrator role',
            createdDate: new Date().toISOString(),
            lastUpdatedDate: new Date().toISOString(),
            permissions: [],
            isDefault: true,
            active: true,
          },
        ],
        totalCount: 2,
      };
    }
    return response;
  });
};

export interface CreateBulkUsersPayload {
  emails: string[];
  roles: string[];
  organizationIds: string[];
}

/**
 * Function to create multiple users in bulk via the CreateBulk API.
 *
 * @param {CreateBulkUsersPayload} payload - The payload containing emails, roles, and organization IDs.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ isSuccess: boolean, errors?: unknown }>} - A promise that resolves to the bulk creation result.
 */
export const createBulkUsers = (payload: CreateBulkUsersPayload, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.createBulkUser}`;

  return clients.post<{
    isSuccess: boolean;
    errors?: unknown;
  }>(url, config.blocksKey, JSON.stringify(payload));
};

export function compareValues(a: number, b: number) {
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  } else {
    return 0;
  }
}

export const createGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.addOrUpdateUserGroup}`;

    const payload = {
        ...data,
        ProjectKey: config.blocksKey,
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const getGroupsAndUsers = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.getGroupsAndUsers}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};


export const getGroupById = async (
    data: any,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.getGroupById}`;
    return clients.post(url, config.blocksKey, JSON.stringify(data));
};


export const removeUserFromGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.removeUserFromGroup}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};


export const deleteIamGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.deleteGroup}?ItemId=${data.groupId}`;

    return clients.delete(url, config.blocksKey);
};

export const getGroupsByIds = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.getGroupsByIds}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};
